<template>
    <div class="container" v-on:keyup="catchInput($event)">
        <div class="page-content mt-10">
            <div class="row">
                <div class="col-12">
                    <h1 class="page-heading text-dark">
                        {{ $t('wordle.title') }}
                    </h1>
                </div>

                <div class="col-12" v-if="!wordleRunning">
                    <div class="alert alert-light" v-if="solution.length === 0">
                        {{ $t('wordle.greeting') }}<br/>
                        {{ $t('wordle.info') }} <currency-icon/>
                    </div>
                    <div :class="'alert alert-'+(win ? 'success' : 'light')" v-else>
                        <strong>{{ win ? $t('wordle.correct') : $t('wordle.incorrect') }}</strong><br/>
                        {{ $t('wordle.solutionWas', {name: solution}) }}
                        <p class="text-center">
                            <img class="pokemon-preview-image" :src="solutionEntry.animated_artwork ?? solutionEntry.front_sprite_url" alt="solution pokemon artwork">
                        </p>
                    </div>
                </div>

<!--                <h5>{{ $t('wordle.selectDifficulty') }}</h5>-->
                <div class="col-12" v-if="!wordleRunning">
                    <a href="javascript:" class="btn btn-lg btn-info w-100" v-on:click="startWordle()">{{ $t('wordle.newRound') }}</a>
                    <a href="javascript:" class="btn btn-light w-100 mt-2" v-on:click="showCanvas()">{{ $t('wordle.possibleEntries') }}</a>
                </div>

                <div class="col-12 row game-wrapper" v-if="wordleRunning">
                    <div class="col-12 row p-0 attempt-line text-center" v-for="(attempt, attemptIndex) in attempts">
                        <div class="col-auto">
                            <div v-for="(letter, letterIndex) in attempt"
                                 :class="'wordle-letter-input '+(getFieldClass(letter))"
                                 v-html="letter.input"
                                 :id="'letter-'+attemptIndex+'-'+letterIndex"
                                 v-on:input="checkInputValue($event, letter)">
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-12 keyboard" v-if="wordleRunning">
                    <div class="row text-center keyboard-line" v-for="(line, lineIndex) in keyboardForLanguage">
                        <a v-for="letter in line" href="javascript:" :class="'col-auto btn btn-sm btn-light keyboard-button m-1 '+(deadKeys.includes(letter) ? 'dead-key' : '')" v-on:click="typeLetter(letter)">
                            {{ letter }}
                        </a>
                        <a v-if="lineIndex === 2" href="javascript:" class="col-auto btn btn-sm btn-light keyboard-button m-1" v-on:click="del()">
                            <i class="fa-solid fa-delete-left"></i>
                        </a>
                        <a v-if="lineIndex === 2 && currentAttemptIndex >= 1" href="javascript:" class="col-auto btn btn-sm btn-light keyboard-button m-1" v-on:click="showCanvas()">🆘</a>
                    </div>
                </div>
            </div>
        </div>

        <div class="offcanvas offcanvas-start" tabindex="-1" id="possible-entries-offcanvas" aria-labelledby="possible-entries-offcanvas-label">
            <div class="offcanvas-header">
                <h5 class="offcanvas-title" id="possible-entries-offcanvas-label">{{ $t('wordle.possibleEntries') }}</h5>
                <button type="button" class="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
            </div>
            <div class="offcanvas-body">
                <div v-for="(entries, entriesLetterCount) in possibleEntries">
                    <div class="row" v-if="!wordleRunning || (wordleRunning && entriesLetterCount === letterCount)">
                        <div class="col-12">
                            <h5>{{ entriesLetterCount + ' ' + $t('wordle.letterCount') }}</h5>
                        </div>
                        <div class="col-6 text-center" v-for="entry in entries">
                            <div class="possible-pokemon-preview-image-wrapper">
                                <img class="possible-pokemon-preview-image" :src="(entry.animated_artwork ?? entry.front_sprite_url)" :alt="entry.name"/>
                            </div>
                            <p>
                                {{ getTranslatedName(entry) }}<br/>
                                <small class="muted">#{{ entry.number }}</small>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CurrencyIcon from "../parts/CurrencyIcon.vue";

const EMPTY_FIELD_VALUE = '&nbsp;';

export default {
    name: "PokeWordle",
    components: {CurrencyIcon},
    data() {
        return {
            wordleRunning: false,
            validEntries: [],
            solutionEntry: {},
            solution: '',
            attempts: [
                [],
                [],
                [],
                [],
                [],
            ],
            currentAttemptIndex: 0,
            currentLetterIndex: 0,
            letterCount: 5,
            keys: {
                en: 'QWERTYUIOP,ASDFGHJKL,ZXCVBNM-',
                de: 'QWERTZUIOP,ASDFGHJKL,YXCVBNM-'
            },
            deadKeys: [],
            win: false,
            possibleEntries: {},
        };
    },
    mounted() {
        this.$store.commit('updateUserData');
        this.fetchPossibleEntries();

        document.body.addEventListener('keyup', (event) => {
            if (this.wordleRunning) {
                this.catchInput(event);
            }
        })
    },
    computed: {
        currentAttempt() {
            return this.attempts[this.currentAttemptIndex];
        },
        keyboardForLanguage() {
            let keys = this.$i18n.locale === 'de' ? this.keys.de : this.keys.en;
            return keys.split(',').map((l) => [...l]);
        }
    },
    watch: {
        //
    },
    methods: {
        showCanvas() {
            $("#possible-entries-offcanvas").offcanvas('toggle');
        },
        startWordle() {
            this.fetchWordle();
        },
        fetchWordle() {
            axios.get('/api/wordle').then(response => {
                this.solutionEntry = response.data.solutionEntry;
                this.validEntries = response.data.validEntries;

                this.letterCount = response.data.letterCount;
                this.generateEmptyFields();

                this.solution = this.getTranslatedName(this.solutionEntry).toUpperCase();
                this.currentAttemptIndex = 0;
                this.currentLetterIndex = 0;
                this.deadKeys = [];
                this.wordleRunning = true;

                setTimeout(() => {
                    document.getElementById('letter-0-0').focus();
                },100)
            })
        },
        fetchPossibleEntries() {
            axios.get('/api/wordle/possible').then(response => {
                this.possibleEntries = response.data
            })
        },
        generateEmptyFields() {
            this.attempts = [[], [], [], [], []];

            this.attempts.forEach((attempt, attemptIndex) => {
                for (let i = 0; i < this.letterCount; i++) {
                    attempt[i] = {
                        index: i,
                        attemptIndex: attemptIndex,
                        correct: null,
                        correctPosition: null,
                        input: EMPTY_FIELD_VALUE,
                        done: false,
                    };
                }
            });
        },
        catchInput(event) {
            const isLetter = /^[a-zA-Z]$/.test(event.key);
            const isHyphen = event.key === '-';
            const isBackspace = event.key === 'Backspace';

            if (isBackspace) {
                this.del();
            } else if (isLetter || isHyphen) {
                this.typeLetter(event.key.toUpperCase())
            }
        },
        typeLetter(letter) {
            this.currentAttempt[this.currentLetterIndex].input = letter;
            this.checkInputValue();
        },
        del() {
            if (this.currentLetterIndex !== 0) {
                if (this.currentLetterIndex === this.letterCount -1 && this.currentAttempt[this.currentLetterIndex].input !== EMPTY_FIELD_VALUE) {
                    this.currentAttempt[this.currentLetterIndex].input = EMPTY_FIELD_VALUE;
                } else {
                    this.currentAttempt[this.currentLetterIndex-1].input = EMPTY_FIELD_VALUE;
                    this.currentLetterIndex--;
                }
            }
        },
        checkInputValue() {
            let letter = this.currentAttempt[this.currentLetterIndex];

            if (letter.index === (this.letterCount - 1)) {
                this.checkIfSubmissionIsValid();
            } else {
                this.currentLetterIndex++;
            }
        },
        checkIfSubmissionIsValid() {
            let submission = '';

            this.currentAttempt.forEach((letter) => {
                submission += letter.input;
            });

            submission = submission.toUpperCase();

            if (!this.validEntries.map(pokdexEntry => this.getTranslatedName(pokdexEntry).toUpperCase()).includes(submission)) {
                iziToast.warning({title: this.$i18n.t('wordle.invalidSubmission')});
                return false;
            }

            if (submission === this.solution) {
                // iziToast.success({title: this.$i18n.t('wordle.correct')});
                this.win = true;
                this.endGame();
            } else {
                this.currentAttempt.forEach(letter => {
                    let correctPosition = [...this.solution][letter.index] === letter.input;
                    let included = [...this.solution].includes(letter.input);

                    letter.correctPosition = correctPosition;
                    letter.correct = included;

                    if (!correctPosition && !included) {
                        this.deadKeys.push(letter.input);
                    }
                });

                this.currentAttempt.done = true;

                if (this.currentAttemptIndex === this.attempts.length - 1) {
                    this.endGame();
                    return false;
                } else {
                    iziToast.warning({title: this.$i18n.t('wordle.incorrect')});
                }

                this.currentAttemptIndex++;
                this.currentLetterIndex = 0;
                // setTimeout(() => {
                //     document.getElementById('letter-'+this.currentAttemptIndex+'-0').focus();
                // }, 100)
            }
        },
        endGame() {
            this.wordleRunning = false;

            if (this.win) {
                axios.post('/api/wordle/submit', {
                    currentAttemptIndex: this.currentAttemptIndex,
                    solution: this.solution,
                }).then((response) => {
                    if (response.data.payout > 0) {
                        let title = this.$i18n.t('wordle.winNotificaton', {payout: response.data.payout}) + '<img style="max-height: 1.5em;" src="/assets/dipocaco/currency_x64.png" alt="currency-icon">';

                        iziToast.success({
                            title: title,
                            iconUrl: ''
                        });

                        this.fetchLeaderboard()
                    }
                })
            }
        },
        getTranslatedName(pokedexEntry) {
            return this.$i18n.locale === 'de' ? pokedexEntry.name_de : pokedexEntry.name;
        },
        getFieldClass(letter) {
            if (letter.index === this.currentLetterIndex && letter.attemptIndex === this.currentAttemptIndex) {
                return ' active';
            }

            return letter.correctPosition ? ' position-correct' : (letter.correct ? ' correct' : (this.currentAttemptIndex > letter.attemptIndex ? ' done' : ''))
        }
    },
};
</script>


<style scoped lang="scss">
.page-heading {
    background-image: url(/assets/dipocaco/pocas/wordle-poca_sm.jpg);
    background-position: center 45%;
    height: 100px !important;
}

.attempt-line {
    text-align: center;
    white-space: nowrap;
    margin-bottom: 0.5em;
    justify-content: center;

    .wordle-letter-input {
        background-color: white;
        color: black;
        text-transform: uppercase;
        display: inline-block;
        padding: 0.7em 0;
        height: 3.5em;
        max-width: 3.5em;
        min-width: 2em;
        font-size: 1.25em;
        text-align: center;

        &:not(:last-child) {
            margin-right: 0.5em;
        }

        &:disabled {
            background-color: lightgrey;
            color: white !important;
        }

        &:is(.active) {
            background-color: lightblue;
            color: white !important;
        }

        &:is(.done) {
            background-color: slategrey;
            color: white !important;
        }

        &:is(.position-correct) {
            background-color: darkolivegreen;
            color: white !important;
        }

        &:is(.correct) {
            background-color: darkgoldenrod;
            color: white !important;
        }
    }

    .pokemon-preview-image {
        max-height: 7.5em;
    }
}

.possible-pokemon-preview-image-wrapper {
    display: flex;
    height: 10em;
    justify-content: center;
    align-items: flex-end;

    .possible-pokemon-preview-image {
        max-width: 100%;
    }
}

.keyboard {
    margin-top: 3em;
    max-width: 98vw !important;

    .keyboard-line {
        display: flex;
        flex-wrap: nowrap;
        justify-content: center;

        .keyboard-button {
            display: inline-block !important;
            text-align: center;
            width: 2.1em !important;
            padding-left: 0;
            padding-right: 0;

            &:is(.dead-key) {
                background-color: dimgrey;
                color: lightgrey;
            }
        }
    }
}
</style>
